import type { ChangeEvent, KeyboardEvent } from "react";

/**
 * The optional one-line reason field (GRA-135): a thesis ("higher low
 * forming", "overextended") jotted down before committing a guess or a
 * trade, then shown back on the Reveal banner and stored with the Round in
 * history. Entirely skippable: an empty note is simply omitted from the
 * history entry (roundLoop's `submit` trims and drops it), so ignoring the
 * field costs nothing.
 *
 * Kept visually quiet on purpose: the footer already carries a rule sentence,
 * the guess/trade controls, and (post-reveal) the replay-step row, so this
 * is one thin, low-contrast line rather than a labeled form field competing
 * for attention with the actual decision.
 *
 * The field's own keydown handler stops the event from bubbling to
 * `window` before it reaches: it's the same input element every mode's
 * global shortcut listener would otherwise see (GuessControls' up/down keys
 * and letter pairs, TradePanel's arrow-key side-pick and Enter-to-place,
 * LoopShell's Enter-to-advance). Typing "u", an arrow key, or Enter here
 * stays inside the field.
 */
export type NoteFieldProps = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /**
   * Replaces the default box styling. The Trade panel sets its own, since
   * there the field sits in a row beside the trade summary and has to match
   * the height and border of the controls above it rather than float centered
   * on its own line.
   */
  className?: string;
};

const MAX_LENGTH = 140;

const DEFAULT_CLASS =
  "min-h-8 w-full max-w-[16rem] rounded border border-muted/15 bg-transparent px-2 py-1 text-center font-mono text-xs text-muted placeholder:text-dim focus:border-muted/40 focus:text-fg focus:outline-none sm:min-h-0";

export function NoteField({ value, onChange, disabled, className }: NoteFieldProps) {
  const stop = (event: KeyboardEvent<HTMLInputElement>) => event.stopPropagation();

  return (
    <input
      type="text"
      inputMode="text"
      aria-label="reason (optional)"
      placeholder="reason (optional)"
      value={value}
      disabled={disabled}
      maxLength={MAX_LENGTH}
      onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
      onKeyDown={stop}
      className={className ?? DEFAULT_CLASS}
    />
  );
}
